// src/components/ResetPassword.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabaseClient';
import LoadingModal from './LoadingModal';
import Logo from '../images/HouvalDark.png'; // Adjust path as needed
import loginBG from '../images/loginBG.jpg'; // Import the background image

const ResetPassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session) {
        // User is authenticated
        setIsLoading(false);
      } else {
        // User is not authenticated
        setError('Invalid or expired password reset link.');
        setIsLoading(false);
      }
    };
    checkSession();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Basic validation
    if (!newPassword || !confirmPassword) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      // Update the user's password
      const { data, error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        setError(error.message || 'Error resetting password.');
      } else {
        setMessage('Your password has been reset successfully.');
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      console.error('Password Reset Error:', err);
    }
    setIsLoading(false);
  };

  const handleNavigate = () => {
    navigate('/portal');
  };

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center text-white relative"
        style={{
          backgroundImage: `url(${loginBG})`, // Use the imported image
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Loading Modal */}
        <LoadingModal loadingModalVisible={isLoading} />

        {/* Overlay */}
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-black bg-opacity-60 z-0"></div>

        {/* Reset Password Container */}
        <div className="relative z-10 bg-gray-50 p-8 rounded-md shadow-lg w-full max-w-md text-center">
          {/* Logo */}
          <div className="mb-6">
            <img src={Logo} alt="Logo" width="150" />
          </div>

          {/* Reset Password Form */}
          {!error ? (
            <form onSubmit={handleSubmit} className="w-full">
              {/* New Password Field */}
              <div className="mb-4">
                <label
                  htmlFor="newPassword"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  New Password
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300"
                  id="newPassword"
                  type="password"
                  placeholder="Enter New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
              </div>

              {/* Confirm Password Field */}
              <div className="mb-6">
                <label
                  htmlFor="confirmPassword"
                  className="block text-gray-700 text-sm font-bold mb-2"
                >
                  Confirm New Password
                </label>
                <input
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring focus:border-blue-300"
                  id="confirmPassword"
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              {/* Submit Button */}
              <div className="mb-4">
                <button
                  className="bg-yellow-400 text-black font-bold py-2 px-4 w-full flex items-center justify-center rounded focus:outline-none focus:ring focus:border-yellow-300 transition duration-200"
                  type="submit"
                >
                  Reset Password
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <p className="text-red-500 text-xs italic text-center mb-4">
                  {error}
                </p>
              )}

              {/* Success Message */}
              {message && (
                <div>
                  <p className="text-green-500 text-xs italic text-center mb-4">
                    {message}
                  </p>
                  <button
                    className="bg-yellow-400 text-black font-bold py-2 px-4 w-full flex items-center justify-center rounded focus:outline-none focus:ring focus:border-yellow-300 transition duration-200"
                    onClick={handleNavigate}
                  >
                    Go to Portal
                  </button>
                </div>
              )}
            </form>
          ) : (
            // Invalid or expired link message
            <div>
              <p className="text-red-500 text-sm mb-4">{error}</p>
              <button
                className="bg-yellow-400 text-black font-bold py-2 px-4 w-full flex items-center justify-center rounded focus:outline-none focus:ring focus:border-yellow-300 transition duration-200"
                onClick={() => navigate('/')}
              >
                Go to Login
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
